import React, { useEffect, useState } from 'react';
import {
  Plus,
  RefreshCw,
  Trash2,
  Edit,
  Cpu,
  Sliders,
  Layers,
} from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import Drawer from '../../../components/common/Drawer';
import StatusChip from '../../../components/common/StatusChip';
import LoadingSkeleton from '../../../components/common/LoadingSkeleton';
import ErrorState from '../../../components/common/ErrorState';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import SourceCombobox from '../../../components/common/SourceCombobox';
import RuleCard from '../components/rules/RuleCard';
import {
  mamGroupService,
  mamManagerService,
  mamRuleService,
} from '../services/mamService';
import {
  getAllocationMethodBadgeClass,
  formatCurrency,
  calculateTradeAllocationPreview,
} from '../utils';
import { MOCK_SIMULATOR_SUB_ACCOUNTS } from '../mock';
import type {
  MAMGroup,
  MAMManager,
  AllocationRule,
  AllocationMethodType,
} from '../types';

export default function MAMAllocation() {
  const [groups, setGroups] = useState<MAMGroup[]>([]);
  const [managers, setManagers] = useState<MAMManager[]>([]);
  const [rules, setRules] = useState<AllocationRule[]>([]);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<MAMGroup | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    managerId: '',
    allocationMethod: 'equity_ratio' as AllocationMethodType,
    minDeposit: 1000,
    maxClients: 50,
  });

  const [simMasterVolume, setSimMasterVolume] = useState(10.0);
  const [simMethod, setSimMethod] = useState<
    'equity_ratio' | 'balance_ratio' | 'fixed_lot'
  >('equity_ratio');

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    groupId: string;
    groupName: string;
    loading?: boolean;
  }>({
    isOpen: false,
    groupId: '',
    groupName: '',
  });

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [grpRes, mgrRes, ruleRes] = await Promise.all([
        mamGroupService.getAll(),
        mamManagerService.getAll(),
        mamRuleService.getAll(),
      ]);
      setGroups(grpRes);
      setManagers(mgrRes);
      setRules(ruleRes);
      if (ruleRes.length > 0) {
        setSelectedRuleId(ruleRes[0].id);
      }
    } catch {
      setError('Failed to load MAM Allocation data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenCreateDrawer = () => {
    setEditingGroup(null);
    setFormData({
      name: '',
      description: '',
      managerId: managers[0]?.id || '',
      allocationMethod: 'equity_ratio',
      minDeposit: 1000,
      maxClients: 50,
    });
    setIsDrawerOpen(true);
  };

  const handleOpenEditDrawer = (grp: MAMGroup) => {
    setEditingGroup(grp);
    setFormData({
      name: grp.name,
      description: grp.description || '',
      managerId: grp.managerId || '',
      allocationMethod: grp.allocationMethod,
      minDeposit: grp.minDeposit || 1000,
      maxClients: grp.maxClients || 50,
    });
    setIsDrawerOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;
    try {
      if (editingGroup) {
        await mamGroupService.update(editingGroup.id, formData);
      } else {
        await mamGroupService.create(formData);
      }
      setIsDrawerOpen(false);
      loadData();
    } catch {
      alert('Action failed');
    }
  };

  const confirmDelete = async () => {
    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      await mamGroupService.delete(deleteModal.groupId);
      setGroups(prev => prev.filter(g => g.id !== deleteModal.groupId));
    } finally {
      setDeleteModal({ isOpen: false, groupId: '', groupName: '' });
    }
  };

  const simPreview = calculateTradeAllocationPreview(
    simMasterVolume,
    MOCK_SIMULATOR_SUB_ACCOUNTS,
    simMethod
  );
  const totalSimAllocated = simPreview.reduce(
    (sum, item) => sum + item.allocatedLots,
    0
  );

  const columns: DataTableColumn<MAMGroup>[] = [
    {
      id: 'name',
      header: 'Group Name',
      sortable: true,
      accessor: grp => (
        <div>
          <p className="font-bold text-foreground text-xs">{grp.name}</p>
          <p className="text-[10px] text-muted-foreground truncate max-w-[200px]">
            {grp.description || 'PAMM/MAM Strategy Group'}
          </p>
        </div>
      ),
    },
    {
      id: 'managerName',
      header: 'Manager',
      sortable: true,
      accessor: grp => (
        <span className="font-semibold text-xs">{grp.managerName}</span>
      ),
    },
    {
      id: 'allocationMethod',
      header: 'Method',
      sortable: true,
      accessor: grp => (
        <span
          className={`px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${getAllocationMethodBadgeClass(grp.allocationMethod)}`}
        >
          {grp.allocationMethod.replace('_', ' ')}
        </span>
      ),
    },
    {
      id: 'totalEquity',
      header: 'Total Equity',
      sortable: true,
      align: 'right',
      accessor: grp => (
        <span className="font-bold text-foreground">
          {formatCurrency(grp.totalEquity)}
        </span>
      ),
    },
    {
      id: 'totalClients',
      header: 'Clients',
      sortable: true,
      align: 'right',
      accessor: grp => (
        <span>
          {grp.totalClients} / {grp.maxClients}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      align: 'center',
      accessor: grp => <StatusChip status={grp.status} size="sm" />,
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'right',
      accessor: grp => (
        <div className="flex items-center justify-end space-x-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={e => {
              e.stopPropagation();
              handleOpenEditDrawer(grp);
            }}
          >
            <Edit className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-rose-600 hover:text-rose-700"
            onClick={e => {
              e.stopPropagation();
              setDeleteModal({
                isOpen: true,
                groupId: grp.id,
                groupName: grp.name,
              });
            }}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="MAM Allocation" />
        <LoadingSkeleton type="cards" cols={3} />
        <LoadingSkeleton type="table" rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="MAM Allocation" />
        <ErrorState message={error} onRetry={loadData} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6 w-full">
      <PageHeader
        title="MAM Allocation Configuration"
        description="Manage master allocation pools, distribution methods & live trade preview"
      >
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
        <Button
          size="sm"
          className="bg-primary/90 hover:bg-primary/95 text-white"
          onClick={handleOpenCreateDrawer}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Add Allocation Group
        </Button>
      </PageHeader>

      {/* Allocation Groups Table */}
      <div className="space-y-3">
        <h3 className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Layers className="w-4 h-4 text-primary" /> Master Allocation Groups (
          {groups.length})
        </h3>
        <DataTable
          columns={columns}
          data={groups}
          searchKeys={['name', 'managerName', 'allocationMethod']}
          searchPlaceholder="Search allocation groups by name, manager..."
          initialPageSize={5}
        />
      </div>

      {/* Allocation Rule Presets */}
      <div className="space-y-3 pt-4 border-t border-border">
        <h3 className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Sliders className="w-4 h-4 text-indigo-600" /> Supported Allocation
          Methods & Presets
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rules.map(rule => (
            <RuleCard
              key={rule.id}
              rule={rule}
              selected={selectedRuleId === rule.id}
              onSelect={() => setSelectedRuleId(rule.id)}
            />
          ))}
        </div>
      </div>

      {/* Real-time Trade Allocation Simulator */}
      <div className="pt-4 border-t border-border space-y-4">
        <h3 className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Cpu className="w-4 h-4 text-emerald-600" /> Live Lot Distribution
          Simulator
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 bg-card border border-border rounded-xl p-4 shadow-sm space-y-4 text-xs">
            <h4 className="font-bold text-foreground">Simulation Parameters</h4>
            <div>
              <label className="block font-medium text-foreground mb-1">
                Master Trade Volume (Lots)
              </label>
              <Input
                type="number"
                step="0.1"
                min="0.1"
                value={simMasterVolume}
                onChange={e =>
                  setSimMasterVolume(parseFloat(e.target.value) || 0)
                }
              />
            </div>
            <div>
              <label className="block font-medium text-foreground mb-1">
                Allocation Method
              </label>
              <SourceCombobox
                items={[
                  {
                    value: 'equity_ratio',
                    label: 'Equity Ratio (Proportional)',
                  },
                  {
                    value: 'balance_ratio',
                    label: 'Balance Ratio (Proportional)',
                  },
                  { value: 'fixed_lot', label: 'Fixed Lot (Equal Split)' },
                ]}
                value={simMethod}
                onChange={val => setSimMethod(val as any)}
              />
            </div>
            <div className="p-3 bg-muted/40 rounded-lg space-y-1 text-[11px]">
              <p className="text-muted-foreground">
                Master Volume:{' '}
                <strong className="text-foreground">
                  {simMasterVolume} Lots
                </strong>
              </p>
              <p className="text-muted-foreground">
                Total Distributed:{' '}
                <strong className="text-emerald-600">
                  {totalSimAllocated.toFixed(2)} Lots
                </strong>
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 bg-card border border-border rounded-xl p-4 shadow-sm space-y-3">
            <h4 className="text-xs font-bold text-foreground">
              Child Account Distribution Breakdown
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                    <th className="text-left py-2 px-3">Sub-Account</th>
                    <th className="text-right py-2 px-3">Equity</th>
                    <th className="text-right py-2 px-3 font-bold text-primary">
                      Allocated Lots
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {simPreview.map(item => {
                    const acc = MOCK_SIMULATOR_SUB_ACCOUNTS.find(
                      a => a.id === item.subAccountId
                    );
                    return (
                      <tr key={item.subAccountId} className="hover:bg-muted/20">
                        <td className="py-2.5 px-3 font-medium text-foreground">
                          {item.subAccountName}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono">
                          {formatCurrency(acc?.equity || 0)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-600">
                          {item.allocatedLots.toFixed(2)} Lots
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Drawer Form for Create/Edit */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={editingGroup ? 'Edit Allocation Group' : 'Add Allocation Group'}
        subtitle="Configure MAM master allocation parameters"
      >
        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div>
            <label className="block font-semibold text-foreground mb-1">
              Group Name *
            </label>
            <Input
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Master Gold Alpha Pool"
              required
            />
          </div>

          <div>
            <label className="block font-semibold text-foreground mb-1">
              Assigned Manager *
            </label>
            <SourceCombobox
              items={managers.map(m => ({
                value: m.id,
                label: `${m.name} (${m.accountNumber})`,
              }))}
              value={formData.managerId}
              placeholder="Select Manager"
              onChange={val => setFormData({ ...formData, managerId: val })}
            />
          </div>

          <div>
            <label className="block font-semibold text-foreground mb-1">
              Allocation Method *
            </label>
            <SourceCombobox
              items={[
                { value: 'equity_ratio', label: 'Equity Ratio' },
                { value: 'balance_ratio', label: 'Balance Ratio' },
                { value: 'percentage', label: 'Percentage' },
                { value: 'fixed_lot', label: 'Fixed Lot' },
              ]}
              value={formData.allocationMethod}
              placeholder="Select Method"
              onChange={val =>
                setFormData({
                  ...formData,
                  allocationMethod: val as AllocationMethodType,
                })
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-foreground mb-1">
                Min Deposit ($)
              </label>
              <Input
                type="number"
                value={formData.minDeposit}
                onChange={e =>
                  setFormData({
                    ...formData,
                    minDeposit: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div>
              <label className="block font-semibold text-foreground mb-1">
                Max Clients
              </label>
              <Input
                type="number"
                value={formData.maxClients}
                onChange={e =>
                  setFormData({
                    ...formData,
                    maxClients: parseInt(e.target.value) || 10,
                  })
                }
              />
            </div>
          </div>

          <div>
            <label className="block font-semibold text-foreground mb-1">
              Description
            </label>
            <Input
              value={formData.description}
              onChange={e =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Optional strategy description"
            />
          </div>

          <div className="pt-4 flex items-center justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDrawerOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-primary/90 hover:bg-primary/95 text-white"
            >
              {editingGroup ? 'Save Changes' : 'Create Allocation Group'}
            </Button>
          </div>
        </form>
      </Drawer>

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Delete Allocation Group"
        message={`Are you sure you want to delete "${deleteModal.groupName}"?`}
        type="danger"
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleteModal.loading}
        onConfirm={confirmDelete}
        onCancel={() =>
          setDeleteModal({ isOpen: false, groupId: '', groupName: '' })
        }
      />
    </div>
  );
}
